export const MENU_ANALYTICS_EVENT_TYPES = [
  'view',
  'scan',
  'download',
  'share',
  'action',
  'session_start',
  'session_end',
  'qr_session_resolved',
  'table_session_view',
  'counter_session_view',
  'guest_action_clicked',
  'order_started',
  'order_submitted',
  'feedback_submitted',
  'service_request_submitted',
  'item_impression',
  'item_impressions',
  'item_opened',
  'item_added',
  'item_removed',
  'cart_abandoned',
  'search_performed',
  'category_viewed',
] as const;

export const PUBLIC_MENU_ACTION_TYPES = [
  'call',
  'directions',
  'website',
  'whatsapp',
  'reservation',
  'order',
  'delivery',
  'review',
  'payment',
  'promotion_claim',
  'service_request',
] as const;

export type MenuAnalyticsEventType = (typeof MENU_ANALYTICS_EVENT_TYPES)[number];
export type PublicMenuActionType = (typeof PUBLIC_MENU_ACTION_TYPES)[number];

type TrackPublicMenuEventInput = {
  menuId: string;
  eventType: MenuAnalyticsEventType;
  path?: string;
  actionType?: PublicMenuActionType;
  actionTarget?: string;
  metadata?: Record<string, unknown>;
  transport?: 'fetch' | 'beacon';
  sessionIdOverride?: string;
};

export type PublicMenuAnalyticsSession = {
  menuId?: string;
  sessionId: string;
  startedAt: number;
};

// Module-level singleton , survives hot-reloads in dev and may hold stale
// session state after an HMR update. If analytics events stop firing after
// repeated saves, restart the dev server.
let activePublicMenuSession: PublicMenuAnalyticsSession | null = null;

// Per-tab stable session id, persisted across reloads so a focus-back / page
// reload does NOT get a new sessionId (which would defeat the page-level
// deduplication of `session_start` / `view` events and re-count the same
// visitor as a fresh session on every reload). Scoped to (menu, tab); a new
// tab or cross-menu navigation legitimately starts a fresh session.
const SS_KEY_PREFIX = 'mnrq_pma_ss_v1:';
function getOrCreateSessionId(menuId?: string): string {
  if (typeof window === 'undefined') {
    return createSessionId();
  }
  const key = `${SS_KEY_PREFIX}${menuId ?? ''}`;
  try {
    const existing = sessionStorage.getItem(key);
    if (existing) {
      return existing;
    }
    const id = createSessionId();
    sessionStorage.setItem(key, id);
    return id;
  } catch { /* private mode, quota, etc. , fall back to an ephemeral id */ }
  return createSessionId();
}

// One `session_start` per (browser, menu) , stored in localStorage so it
// survives hard reloads (which destroy the in-memory + sessionStorage state
// on other tabs). A visitor who reloads 10x does not emit 10 session_starts.
const VISITED_PREFIX = 'mnrq_pma_visited_v1:';

function createSessionId() {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `mnrq-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function createSession(menuId?: string): PublicMenuAnalyticsSession {
  return {
    menuId,
    sessionId: getOrCreateSessionId(menuId),
    startedAt: Date.now(),
  };
}

function getClientAnalyticsSession(menuId?: string) {
  if (typeof window === 'undefined') {
    return null;
  }

  if (!activePublicMenuSession) {
    activePublicMenuSession = createSession(menuId);
    return activePublicMenuSession;
  }

  if (menuId && activePublicMenuSession.menuId !== menuId) {
    activePublicMenuSession = createSession(menuId);
  }

  return activePublicMenuSession;
}

/**
 * Start (or reuse) the public-menu analytics session for `menuId`.
 *
 * Returns the session plus `isNewVisit` , true only the FIRST time this menu
 * is viewed in this browser (guarded by localStorage, surviving hard reloads).
 * Callers emit a one-time `session_start` event only when `isNewVisit` is true;
 * repeated reloads/focus-back of the same menu reuse the in-memory session and
 * do NOT re-emit, so the same visitor is not counted per refresh.
 */
export function beginPublicMenuAnalyticsSession(menuId: string): { session: PublicMenuAnalyticsSession; isNewVisit: boolean } | null {
  if (typeof window === 'undefined') {
    return null;
  }

  if (!activePublicMenuSession) {
    activePublicMenuSession = createSession(menuId);
  } else if (activePublicMenuSession.menuId !== menuId) {
    activePublicMenuSession = createSession(menuId);
  }

  const visitedKey = `${VISITED_PREFIX}${menuId}`;
  let isNewVisit = false;
  try {
    if (localStorage.getItem(visitedKey) === null) {
      isNewVisit = true;
      localStorage.setItem(visitedKey, '1');
    }
  } catch { /* private mode, quota , treat as new visit but keep going */ }

  return { session: activePublicMenuSession, isNewVisit };
}

function sendBeaconPayload(body: Record<string, unknown>) {
  if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') {
    return false;
  }

  try {
    const blob = new Blob([JSON.stringify(body)], {
      type: 'application/json',
    });
    return navigator.sendBeacon(resolveApiRequestUrl('/api/public/analytics'), blob);
  } catch {
    return false;
  }
}

export async function trackPublicMenuEvent(input: TrackPublicMenuEventInput): Promise<void> {
  if (typeof window === 'undefined' || !input.menuId) {
    return;
  }

  const session = getClientAnalyticsSession(input.menuId);
  const path = input.path || `${window.location.pathname}${window.location.search}`;
  const body = {
    menuId: input.menuId,
    eventType: input.eventType,
    sessionId: input.sessionIdOverride || session?.sessionId || null,
    path,
    actionType: input.actionType,
    actionTarget: input.actionTarget,
    metadata: input.metadata || undefined,
  };

  try {
    if (input.transport === 'beacon' && sendBeaconPayload(body)) {
      return;
    }

    await fetch(resolveApiRequestUrl('/api/public/analytics'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      cache: 'no-store',
      keepalive: true,
      body: JSON.stringify(body),
    });
  } catch {
    // Analytics failures should never block the user journey.
  }
}

// --- Unified analytics event helpers (docs/expansion-roadmap-2026/44-unified-analytics.md, D4) ---
// These map the declared-but-dead and new guest-behaviour events onto the single event spine.
// menu_item_id / order_id are sent in metadata and the ingestion route promotes them onto the
// dedicated columns so the Items tab can answer "opened vs ordered" without a join.

function trackMenuItemEvent(
  menuId: string,
  eventType: MenuAnalyticsEventType,
  menuItemId: string | null | undefined,
  metadata: Record<string, unknown> = {},
) {
  if (!menuItemId) return;
  return trackPublicMenuEvent({
    menuId,
    eventType,
    metadata: { menu_item_id: menuItemId, ...metadata },
  });
}

export function trackItemImpression(
  menuId: string,
  menuItemId: string,
  options: { position?: number; category?: string } = {},
) {
  return trackMenuItemEvent(menuId, 'item_impression', menuItemId, {
    position: options.position,
    category: options.category,
  });
}

// Coalesced scroll-settle flush: callers collect impressions during a scroll and
// flush once. All items ride a single request (`item_impressions`) , one POST per
// scroll-settle instead of one POST per card.
export function trackItemImpressions(
  menuId: string,
  impressions: Array<{ menuItemId: string; position?: number; category?: string }>,
) {
  if (impressions.length === 0) return;
  return trackPublicMenuEvent({
    menuId,
    eventType: 'item_impressions',
    metadata: {
      items: impressions.map((impression) => ({
        menu_item_id: impression.menuItemId,
        position: impression.position ?? null,
        category: impression.category ?? null,
      })),
    },
  });
}

export function trackItemOpened(
  menuId: string,
  menuItemId: string,
  options: { dwellMs?: number } = {},
) {
  return trackMenuItemEvent(menuId, 'item_opened', menuItemId, {
    dwell_ms: options.dwellMs,
  });
}

export function trackItemAdded(
  menuId: string,
  menuItemId: string,
  options: { qty?: number } = {},
) {
  return trackMenuItemEvent(menuId, 'item_added', menuItemId, {
    qty: options.qty ?? 1,
  });
}

export function trackItemRemoved(
  menuId: string,
  menuItemId: string,
  options: { qty?: number } = {},
) {
  return trackMenuItemEvent(menuId, 'item_removed', menuItemId, {
    qty: options.qty ?? 1,
  });
}

export function trackCartAbandoned(
  menuId: string,
  options: { itemIds?: string[]; subtotal?: number; transport?: 'fetch' | 'beacon' } = {},
) {
  return trackPublicMenuEvent({
    menuId,
    eventType: 'cart_abandoned',
    transport: options.transport,
    metadata: {
      item_ids: options.itemIds ?? [],
      subtotal: options.subtotal ?? 0,
    },
  });
}

export function trackSearchPerformed(
  menuId: string,
  options: { term: string; resultCount: number },
) {
  return trackPublicMenuEvent({
    menuId,
    eventType: 'search_performed',
    metadata: {
      term: options.term,
      result_count: options.resultCount,
    },
  });
}

export function trackCategoryViewed(
  menuId: string,
  options: { category: string; dwellMs?: number },
) {
  return trackPublicMenuEvent({
    menuId,
    eventType: 'category_viewed',
    metadata: {
      category: options.category,
      dwell_ms: options.dwellMs,
    },
  });
}
import { resolveApiRequestUrl } from '@/lib/routing/api-requests';
